"use client";

import { useState } from "react";
import { X, Loader2, ArrowRight, Calendar, FileText, CheckCircle2, ShieldCheck } from "lucide-react";
import { TelecallingLead } from "@/lib/demos/telecallingDb";

interface TelecallingStageModalProps {
  isOpen: boolean;
  onClose: () => void;
  lead: TelecallingLead;
  targetStage: string;
  onConfirm: (updates: Partial<TelecallingLead>) => Promise<void>;
}

export function TelecallingStageModal({
  isOpen,
  onClose,
  lead,
  targetStage,
  onConfirm,
}: TelecallingStageModalProps) {
  // Discovery Meeting fields
  const [meetingTime, setMeetingTime] = useState(lead.demo_meeting_time || "Tomorrow at 03:00 PM IST");
  const [closerAssigned, setCloserAssigned] = useState(lead.closer_assigned || "Aakash Verma (Enterprise Lead)");
  const [meetingLink, setMeetingLink] = useState(lead.meeting_link || `https://meet.google.com/voc-${lead.lead_no.toLowerCase().replace(/[^a-z0-9]/g, "")}`);

  // Deal Won / Retainer Invoice fields
  const [msaContractId, setMsaContractId] = useState(lead.msa_contract_id || `VOC-MSA-2026-${Math.floor(1000 + Math.random() * 9000)}`);
  const [invoiceNo, setInvoiceNo] = useState(lead.invoice_no || `INV-VOCALIS-2026-${Math.floor(1000 + Math.random() * 9000)}`);
  const [monthlyRetainer, setMonthlyRetainer] = useState(lead.monthly_retainer || 160000);

  const [notes, setNotes] = useState(lead.notes || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const isDemoStage = targetStage === "Demo / Discovery Scheduled";
  const isWonStage = targetStage === "Deal Closed / Retainer Won";
  const sym = lead.currency === "USD" ? "$" : "₹";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const updates: Partial<TelecallingLead> = {
        stage: targetStage as any,
        notes: notes || undefined,
      };

      if (isDemoStage) {
        updates.demo_meeting_time = meetingTime;
        updates.closer_assigned = closerAssigned;
        updates.meeting_link = meetingLink;
      }

      if (isWonStage) {
        updates.msa_contract_id = msaContractId;
        updates.invoice_no = invoiceNo;
        updates.monthly_retainer = Number(monthlyRetainer);
      }

      await onConfirm(updates);
      onClose();
    } catch (error) {
      console.error("[Telecalling Stage Transition Error]", error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md overflow-y-auto">
      <div className="relative w-full max-w-lg rounded-2xl bg-slate-900 border border-slate-800 p-6 sm:p-8 shadow-2xl shadow-cyan-950/50 my-8">
        
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-6">
          <span className="text-[10px] font-mono text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded border border-cyan-500/20">
            {lead.lead_no}
          </span>
          <h3 className="text-lg font-bold text-white mt-2">
            Advance Lead: <span className="text-cyan-400">{targetStage}</span>
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            Prospect: <strong>{lead.prospect_name}</strong> ({lead.company_name})
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* SPECIAL CASE 1: DEMO / DISCOVERY SCHEDULED */}
          {isDemoStage && (
            <div className="space-y-4 p-4 rounded-xl bg-slate-950/80 border border-cyan-500/30">
              <div className="flex items-center gap-2 text-xs font-bold text-cyan-300">
                <Calendar className="w-4 h-4 text-cyan-400" />
                <span>Schedule Executive Discovery Demo</span>
              </div>
              <p className="text-[11px] text-slate-400">
                Advancing to this stage will automatically generate and attach the <strong>Call QA Audit &amp; Performance Pass PDF</strong> to the prospect email.
              </p>

              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">
                  Confirmed Meeting Slot *
                </label>
                <input
                  type="text"
                  required
                  value={meetingTime}
                  onChange={(e) => setMeetingTime(e.target.value)}
                  placeholder="e.g. Tomorrow at 03:00 PM IST"
                  className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-xs focus:outline-none focus:border-cyan-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">
                  Assigned Closer / Account Executive *
                </label>
                <select
                  value={closerAssigned}
                  onChange={(e) => setCloserAssigned(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-xs focus:outline-none focus:border-cyan-500"
                >
                  <option value="Aakash Verma (Enterprise Lead)">Aakash Verma (Enterprise Lead)</option>
                  <option value="David Miller (Senior Closer)">David Miller (Senior Closer)</option>
                  <option value="Priya Sharma (Strategic SDR Lead)">Priya Sharma (Strategic SDR Lead)</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">
                  Meeting Room Link *
                </label>
                <input
                  type="url"
                  required
                  value={meetingLink}
                  onChange={(e) => setMeetingLink(e.target.value)}
                  placeholder="https://meet.google.com/..."
                  className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-xs focus:outline-none focus:border-cyan-500"
                />
              </div>
            </div>
          )}

          {/* SPECIAL CASE 2: DEAL CLOSED / RETAINER WON */}
          {isWonStage && (
            <div className="space-y-4 p-4 rounded-xl bg-slate-950/80 border border-emerald-500/30">
              <div className="flex items-center gap-2 text-xs font-bold text-emerald-300">
                <FileText className="w-4 h-4 text-emerald-400" />
                <span>Execute Retainer Agreement &amp; Generate Tax Invoice</span>
              </div>
              <p className="text-[11px] text-slate-400">
                Advancing to this stage will automatically generate and attach the <strong>Master Services Agreement (MSA) &amp; Seat Retainer Invoice PDF</strong> with settlement wire coordinates.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1">
                    MSA Contract ID *
                  </label>
                  <input
                    type="text"
                    required
                    value={msaContractId}
                    onChange={(e) => setMsaContractId(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-xs focus:outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1">
                    Tax Invoice No *
                  </label>
                  <input
                    type="text"
                    required
                    value={invoiceNo}
                    onChange={(e) => setInvoiceNo(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-xs focus:outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">
                  Final Monthly Retainer Amount ({sym}) *
                </label>
                <input
                  type="number"
                  required
                  value={monthlyRetainer}
                  onChange={(e) => setMonthlyRetainer(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-xs focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>
          )}

          {/* Operational Notes */}
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">
              Calling Floor Operational Notes
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add disposition context, caller feedback, or custom contract clauses..."
              className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-cyan-500 transition-colors"
            />
          </div>

          <div className="pt-3">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold text-xs shadow-lg shadow-cyan-500/20 flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Processing Stage &amp; Dispatches...</span>
                </>
              ) : (
                <>
                  <span>Advance Stage &amp; Trigger Automation</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>

          <div className="text-center text-[10px] text-slate-500 flex items-center justify-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5 text-cyan-400" />
            Automatic SMTP dispatch with PDF attachment to {lead.email}
          </div>
        </form>

      </div>
    </div>
  );
}
